/**
 * Workspace file preview strategy — the single source of truth shared by the
 * backend (which data path to take) and the frontend (gating + which renderer
 * to mount). One table, both sides import it; no implicit FE↔BE contract.
 *
 * `mode` decides the data path:
 *   - "url"     → an inline presigned URL; the browser renders the file
 *                 natively (<img> / <iframe>), no bytes through the backend
 *                 and no CORS (a browser load, not a JS fetch).
 *   - "content" → the backend reads the file (bounded) and returns text we
 *                 transform in JS (rendered markdown, pretty JSON, CSV text).
 *
 * `kind` is a frontend rendering hint only — the backend reads just `mode`.
 */

export type WorkspacePreviewMode = "url" | "content";
export type WorkspacePreviewKind = "markdown" | "text" | "json" | "image" | "iframe";

export interface WorkspacePreviewDescriptor {
  readonly mode: WorkspacePreviewMode;
  readonly kind: WorkspacePreviewKind;
  /**
   * url-mode only: the Content-Type the presigned preview URL forces on the
   * response (`ResponseContentType`). Files written through mount-s3 are
   * stored as `binary/octet-stream`, which browsers download instead of
   * rendering inline — the override makes preview independent of how the
   * object was written.
   */
  readonly contentType?: string;
}

/**
 * Cap for "content" mode: the backend reads at most this many bytes; larger
 * files come back truncated to the first slice with a `truncated` flag.
 */
export const MAX_PREVIEW_CONTENT_BYTES = 1024 * 1024; // 1 MB

/**
 * Cap for "url" mode: the frontend won't load a file larger than this into the
 * browser — it prompts a download instead. No server round-trip needed; the
 * folder listing already carries each file's size.
 */
export const MAX_PREVIEW_URL_BYTES = 25 * 1024 * 1024; // 25 MB

const PREVIEW_BY_EXT: Readonly<Record<string, WorkspacePreviewDescriptor>> = {
  // content — fetched + transformed in JS
  md: { mode: "content", kind: "markdown" },
  markdown: { mode: "content", kind: "markdown" },
  json: { mode: "content", kind: "json" },
  // CSV: browsers force-download `text/csv` from an iframe regardless of
  // disposition, so read + render it as text from our own origin instead.
  csv: { mode: "content", kind: "text" },
  // url — browser renders the file directly from an inline presigned URL
  txt: { mode: "url", kind: "iframe", contentType: "text/plain; charset=utf-8" },
  text: { mode: "url", kind: "iframe", contentType: "text/plain; charset=utf-8" },
  log: { mode: "url", kind: "iframe", contentType: "text/plain; charset=utf-8" },
  pdf: { mode: "url", kind: "iframe", contentType: "application/pdf" },
  png: { mode: "url", kind: "image", contentType: "image/png" },
  jpg: { mode: "url", kind: "image", contentType: "image/jpeg" },
  jpeg: { mode: "url", kind: "image", contentType: "image/jpeg" },
  gif: { mode: "url", kind: "image", contentType: "image/gif" },
  webp: { mode: "url", kind: "image", contentType: "image/webp" },
  svg: { mode: "url", kind: "image", contentType: "image/svg+xml" }
};

/**
 * Every extension the table serves. Exported so a guard can iterate the table
 * itself rather than a hand-written copy of it — a second list is a list that
 * stops matching, and the row it misses is the one nobody thought about.
 */
export const WORKSPACE_PREVIEW_EXTENSIONS = Object.keys(PREVIEW_BY_EXT);

/** Lowercase extension of a filename ("" when there is none). */
export function workspaceFileExtension(name: string): string {
  const idx = name.lastIndexOf(".");
  return idx >= 0 ? name.slice(idx + 1).toLowerCase() : "";
}

export function workspacePreviewFor(name: string): WorkspacePreviewDescriptor | null {
  const ext = workspaceFileExtension(name);
  // `Object.hasOwn`, not a bare index. The extension comes from a user-supplied
  // filename, and a plain object literal inherits from Object.prototype — so
  // `report.constructor` resolved to `Object.prototype.constructor`, a
  // FUNCTION, which is truthy and therefore survived the `?? null`. The caller
  // then received a function where a descriptor was promised:
  // `isWorkspacePreviewable("report.constructor")` returned true and
  // `.mode` was undefined. `__proto__` did the same with Object.prototype.
  // `hasOwnProperty.call`, not `Object.hasOwn` — this package's lib target
  // predates ES2022 and the shorter form does not compile here.
  if (!Object.prototype.hasOwnProperty.call(PREVIEW_BY_EXT, ext)) return null;
  return PREVIEW_BY_EXT[ext] ?? null;
}

export function isWorkspacePreviewable(name: string): boolean {
  return workspacePreviewFor(name) !== null;
}
